/* --external-- */
import React, { useState, useRef, useEffect } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { Dropdown, DropdownMenu, TextInput } from "@toddle-edu/ds-web";
import { CaretDownFilled, ClockOutlined } from "@toddle-edu/ds-icons";

/* --internal-- */
import {
  DURATION_PICKER_OPTIONS,
  FIELD_UID_DURATION,
} from "Worksheet/modules/WorksheetConstants";

/* --relative-- */
import classes from "./DurationPicker.scss";

/**
 * Maximum allowed duration limit in minutes
 */
const MAX_LIMIT = 10;

/**
 * DurationPicker component that allows users to select a duration
 * from a dropdown or input a custom value. Duration values are
 * limited between 1 and MAX_LIMIT (10) minutes.
 *
 * @param {Object} props Component props
 * @returns {React.ReactElement} DurationPicker component
 */
const DurationPicker = props => {
  const {
    value,
    fieldKey,
    updateInputField,
    t,
    questionTypeUid,
    labelConfig,
  } = props;
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [durationValue, setDurationValue] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const textInputRef = useRef(null);

  /**
   * Handles the change in duration input or dropdown selection.
   * Converts the input value (in minutes) to milliseconds and updates the field.
   * Ensures value is between 1 and MAX_LIMIT minutes.
   *
   * @param {string} currValue - The current input value, which can be empty or a number in minutes.
   */
  const handleDurationOnChange = ({ currValue, from = null }) => {
    if (from === "dropdown") {
      setIsDropdownVisible(false);
    }

    // Clean input to integers only
    const cleanValue = _.replace(currValue, /\D/g, "");
    const valueInt = _.toInteger(cleanValue);

    // Clamp values between 1 and MAX_LIMIT if not empty
    if (cleanValue !== "" && (valueInt < 1 || valueInt > MAX_LIMIT)) {
      return;
    }

    const durationValue = cleanValue === "" ? "" : valueInt * 60000;

    const modifiedValue = {
      [fieldKey]: {
        ...value,
        [FIELD_UID_DURATION]: durationValue,
      },
    };

    updateInputField({ params: modifiedValue, isDebounce: true });
  };

  /**
   * Handles blur of the input field when dropdown is open.
   * Ensures value is clamped between 1 and MAX_LIMIT minutes and prevents losing focus
   *
   * @param {string} currValue - The current input value when blur occurs.
   */
  const handleDurationOnBlur = currValue => {
    if (isDropdownVisible) textInputRef.current.focus();
    else setIsEditMode(false);
    const value = _.toInteger(_.replace(currValue, /\D/g, ""));
    // Only update if value is out of range (empty, <1, or >MAX_LIMIT)
    if (currValue === "" || value < 1 || value > MAX_LIMIT) {
      const clampedValue =
        currValue === "" ? "1" : _.toString(_.clamp(value, 1, MAX_LIMIT));
      handleDurationOnChange({ currValue: clampedValue });
    }
  };

  /**
   * Handles keydown events for the duration picker component for accessibility.
   *
   * @param {KeyboardEvent} event - The keyboard event triggered by the user.
   * @property {string} event.key - The key pressed by the user.
   *
   * - Toggles the dropdown visibility when the "Enter" key is pressed.
   * - Hides the dropdown and removes focus from the input field when the "Escape" key is pressed.
   */
  const handleKeyDown = event => {
    const { key } = event;
    setIsDropdownVisible(false);
    if (key === "Enter") {
      if (event?.target?.value) handleDurationOnBlur(event?.target?.value);
      textInputRef.current.blur();
    } else if (key === "Escape") {
      setIsDropdownVisible(false);
      textInputRef.current.blur();
    }
  };

  const labelLocale = _.get(labelConfig, "labelLocale", "");

  const testId = `worksheet-${questionTypeUid}-questionType-durationDropdown`;

  useEffect(() => {
    let duration = _.get(value, FIELD_UID_DURATION);
    // Set default value for duration if not provided to 10 minutes
    if (!duration && duration !== "" && !isEditMode) {
      handleDurationOnChange({ currValue: "10" });
    }
    duration = duration === "" ? "" : duration / 60000; // convert milliseconds to minutes
    setDurationValue(duration);
  }, [value]);

  const formattedDuration = isEditMode
    ? durationValue
    : durationValue !== ""
    ? t("worksheet:time_in_min", { count: durationValue })
    : "";

  return (
    <div className={classes.container}>
      <div className="text-body-1">{t(labelLocale)}</div>
      <Dropdown
        dsVersion="2.0"
        visible={isDropdownVisible}
        onVisibleChange={setIsDropdownVisible}
        overlay={
          <DropdownMenu
            options={DURATION_PICKER_OPTIONS}
            onClick={e =>
              handleDurationOnChange({ currValue: e.key, from: "dropdown" })
            }
            testId={testId}
          />
        }
        className={classes.durationDropdown}
      >
        <div>
          <TextInput
            dsVersion="2.0"
            ref={textInputRef}
            value={formattedDuration}
            onChange={e =>
              handleDurationOnChange({ currValue: e.target.value })
            }
            onBlur={e => handleDurationOnBlur(e.target.value)}
            onFocus={() => setIsEditMode(true)}
            onKeyDown={handleKeyDown}
            leadingIcon={<ClockOutlined />}
            trailingIcon={<CaretDownFilled />}
            testId={testId}
          />
        </div>
      </Dropdown>
    </div>
  );
};

DurationPicker.propTypes = {
  value: PropTypes.object,
  fieldKey: PropTypes.string,
  questionSettings: PropTypes.object,
  updateInputField: PropTypes.func,
  labelConfig: PropTypes.object,
};

DurationPicker.defaultProps = {
  value: {},
  fieldKey: "settings",
  questionSettings: {},
  updateInputField: _.noop,
  t: () => "",
  labelConfig: {},
};

export default DurationPicker;
